'use client';

import { COLORS, PATHS } from '@/constants';
import { useMasterAdmin } from '@/contexts/masterAdmin';
import { Button, Typography } from '@mui/material';
import { useTranslations } from 'next-intl';
import { useRouter } from 'next/navigation';
import { usePathname } from '@/config/i18n/navigation';
import { getBrokerInitials } from '@/features/pages/profile/master-admin/utils';
import styled from 'styled-components';

const BlockWrapper = styled.div`
  border: 1px solid ${COLORS['neutral/300']};
  border-radius: 6px;
  background: ${COLORS['white']};
  overflow: hidden;
`;

const BlockHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background: ${COLORS['neutral/50']};
  border-bottom: 1px solid ${COLORS['neutral/300']};
`;

const BlockBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 12px;
`;

const SectionLabel = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: ${COLORS['primary/900']};
`;

const ImpersonationChip = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 8px;
  border-radius: 4px;
  background: ${COLORS['neutral/50']};
  border: 1px solid ${COLORS['neutral/200']};
`;

const SelectBrokerButton = styled(Button)`
  && {
    background: ${COLORS['secondary/500']};
    box-shadow: none;
    height: 38px;

    &:hover {
      background: ${COLORS['secondary/700']};
      box-shadow: none;
    }

    &.Mui-disabled {
      background: ${COLORS['neutral/200']};
    }
  }
`;

const ReturnAdminButton = styled(Button)`
  && {
    height: 38px;
    box-shadow: none;

    &:hover {
      box-shadow: none;
    }
  }
`;

const Avatar = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: ${COLORS['primary/200']};
  color: ${COLORS['primary/900']};
  font-size: 11px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const ChipInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

export const MasterAdminSidebarBlock = () => {
  const t = useTranslations('masterAdmin');
  const router = useRouter();
  const pathname = usePathname();
  const { isImpersonating, impersonatedBroker, returnToMasterAdmin } = useMasterAdmin();

  const isOnMasterAdminPage = pathname.endsWith(`/${PATHS.MASTER_ADMIN}`);
  const goToMasterAdmin = () => router.push(`/profile/${PATHS.MASTER_ADMIN}`);

  return (
    <BlockWrapper>
      <BlockHeader>
        <SectionLabel>{t('title')}</SectionLabel>
      </BlockHeader>

      <BlockBody>
        {isImpersonating && impersonatedBroker ? (
          <>
            <ImpersonationChip>
              <Avatar>{getBrokerInitials(impersonatedBroker.name)}</Avatar>
              <ChipInfo>
                <Typography fontSize={12} fontWeight={700} color={COLORS['primary/900']} noWrap>
                  {impersonatedBroker.name}
                </Typography>
                <Typography fontSize={11} color={COLORS['neutral/500']} noWrap>
                  {impersonatedBroker.officeName}
                </Typography>
              </ChipInfo>
            </ImpersonationChip>
            <ReturnAdminButton
              variant="outlined"
              size="small"
              color="error"
              fullWidth
              onClick={returnToMasterAdmin}
            >
              {t('returnButton')}
            </ReturnAdminButton>
          </>
        ) : (
          <SelectBrokerButton
            variant="contained"
            size="small"
            fullWidth
            disabled={isOnMasterAdminPage}
            onClick={goToMasterAdmin}
          >
            {t('selectBrokerButton')}
          </SelectBrokerButton>
        )}
      </BlockBody>
    </BlockWrapper>
  );
};
